/**
 * Typed label interface for Advanced Table i18n.
 * Components accept an optional `labels` prop — English defaults built-in.
 */

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface AdvancedTableLabels {
  // Filter
  filterWhere: string;
  filterAnd: string;
  filterAddFilter: string;
  filterClearAll: string;
  filterNoFilters: string;
  filterSelectField: string;
  filterSearchFields: string;
  filterNoFields: string;
  filterOperator: string;
  filterValue: string;
  filterSelect: string;
  filterYes: string;
  filterNo: string;
  filterMin: string;
  filterMax: string;

  // Operator labels — keyed by FilterOperator value
  operatorLabels: Record<string, string>;

  // Sort
  sort: string;
  sortRules: string;
  sortAdd: string;
  sortClearAll: string;
  sortNoRules: string;
  sortSelectField: string;
  sortAscending: string;
  sortDescending: string;

  // Action bar
  actionSelected: string;
  actionSetStatus: string;
  actionEnable: string;
  actionDisable: string;
  actionExportCSV: string;
  actionArchive: string;
  actionCancel: string;
  actionArchiveTitle: string;
  actionArchiveDescription: string;
  actionArchiveConfirm: string;
  actionRestore: string;

  // Toast
  toastEnabled: string;
  toastDisabled: string;
  toastArchived: string;
  toastRestored: string;
  toastFailedUpdate: string;
  toastFailedArchive: string;
  toastFailedRestore: string;
  toastPartialFail: string;

  // Empty state
  emptyTitle: string;
  emptyDescription: string;

  // Column visibility
  columns: string;
  columnsSearch: string;
  columnsEmpty: string;

  // Toolbar
  filters: string;

  // Density
  densityLabel: string;
  densityCompact: string;
  densityDefault: string;
  densityComfortable: string;
  densitySpacious: string;

  // Column pinning
  pinLeft: string;
  pinRight: string;
  unpin: string;

  // Focus mode
  exitFocus: string;
}

// ---------------------------------------------------------------------------
// English defaults
// ---------------------------------------------------------------------------

export const DEFAULT_ADVANCED_TABLE_LABELS: AdvancedTableLabels = {
  // Filter
  filterWhere: 'Where',
  filterAnd: 'and',
  filterAddFilter: 'Add filter',
  filterClearAll: 'Clear all',
  filterNoFilters: 'No filters applied. Click "Add filter" to get started.',
  filterSelectField: 'Select field...',
  filterSearchFields: 'Search fields...',
  filterNoFields: 'No fields found.',
  filterOperator: 'Operator...',
  filterValue: 'Value...',
  filterSelect: 'Select...',
  filterYes: 'Yes',
  filterNo: 'No',
  filterMin: 'Min',
  filterMax: 'Max',

  // Operator labels
  operatorLabels: {
    like: 'Contains',
    equals: 'Is',
    notEquals: 'Is not',
    notLike: 'Does not contain',
    isSet: 'Is not empty',
    isNotSet: 'Is empty',
    greaterThan: 'Greater than',
    lessThan: 'Less than',
    greaterOrEqual: 'Greater or equal',
    lessOrEqual: 'Less or equal',
    between: 'Between',
    in: 'Is any of',
    notIn: 'Is none of',
  },

  // Sort
  sort: 'Sort',
  sortRules: 'Sort rules',
  sortAdd: 'Add sort',
  sortClearAll: 'Clear all',
  sortNoRules: 'No sort rules. Click below to add one.',
  sortSelectField: 'Select field',
  sortAscending: 'Ascending',
  sortDescending: 'Descending',

  // Action bar
  actionSelected: 'selected',
  actionSetStatus: 'Set Status',
  actionEnable: 'Enable',
  actionDisable: 'Disable',
  actionExportCSV: 'Export CSV',
  actionArchive: 'Archive',
  actionCancel: 'Cancel',
  actionArchiveTitle: 'Archive',
  actionArchiveDescription:
    'They will be hidden from active lists but can be restored.',
  actionArchiveConfirm: 'Archive',
  actionRestore: 'Restore',

  // Toast
  toastEnabled: 'enabled',
  toastDisabled: 'disabled',
  toastArchived: 'archived',
  toastRestored: 'restored',
  toastFailedUpdate: 'Failed to update status',
  toastFailedArchive: 'Failed to archive items',
  toastFailedRestore: 'Failed to restore items',
  toastPartialFail: 'items failed to update',

  // Empty state
  emptyTitle: 'No items found',
  emptyDescription: 'Try adjusting your filters or search query.',

  // Column visibility
  columns: 'Columns',
  columnsSearch: 'Search columns...',
  columnsEmpty: 'No columns found.',

  // Toolbar
  filters: 'Filters',

  // Density
  densityLabel: 'Density',
  densityCompact: 'Compact',
  densityDefault: 'Default',
  densityComfortable: 'Comfortable',
  densitySpacious: 'Spacious',

  // Column pinning
  pinLeft: 'Pin to Left',
  pinRight: 'Pin to Right',
  unpin: 'Unpin',

  // Focus mode
  exitFocus: 'Exit',
};
